import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DocumentsTable from '@/features/documents/components/pages/documents-table';
import SearchBar from '@/features/documents/components/search-bar';
import {
  orgaDocumentsQueryKey,
  useDocuments,
} from '@/features/documents/hooks/use-documents';
import PlusIcon from '@/shared/assets/plus.svg?react';
import {
  HubFilter,
  HubFilterOption,
} from '@/shared/components/filter/hub-filter';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { PaginationBar } from '@/shared/components/pagination-bar';
import { Button } from '@/shared/components/ui/button';
import { DocumentUploadDialog } from '@/shared/components/ui/upload/document-upload-dialog';
import {
  createHubFilterStore,
  useGenericActiveProjectHubFilterCount,
  useGenericHasProjectHubFilters,
} from '@/shared/hooks/use-hub-filter-store';
import { usePagination } from '@/shared/hooks/use-pagination';
import { SecureProjectDocument } from '@/shared/types/api';

export const useDocumentsHubFilterStore = createHubFilterStore();
export const useActiveDocumentsHubFilterCount = () =>
  useGenericActiveProjectHubFilterCount(useDocumentsHubFilterStore());
export const useHasDocumentsHubFilters = () =>
  useGenericHasProjectHubFilters(useDocumentsHubFilterStore());

const AddDocumentButton = () => {
  const { t } = useTranslation('document');
  return (
    <>
      <PlusIcon className="h-4 w-4" />
      <p className="hidden sm:block">{t('DocumentHub.add_document')}</p>
    </>
  );
};

const PAGE_SIZE = 10;

const DocumentsHubPage = () => {
  const { t } = useTranslation('document');
  const { data: documents, isLoading, isError } = useDocuments();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<
    SecureProjectDocument | undefined
  >(undefined);
  const { searchQuery, setSearchQuery } = useDocumentsHubFilterStore();

  const { paginatedData, currentPage, totalPages, setCurrentPage } =
    usePagination({ data: documents ?? [], pageSize: PAGE_SIZE });

  const editMode = !!currentDocument;

  const queryClient = useQueryClient();

  return (
    <DefaultLayout
      title={t('DocumentHub.title')}
      headerActions={[
        <SearchBar
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          placeholder={t('DocumentHub.search_placeholder')}
        />,
        <Button
          onClick={() => {
            setDialogOpen(true);
          }}
          variant={'greenGradient'}
          btnType="secondary"
        >
          {<AddDocumentButton />}
        </Button>,
        <HubFilter
          {...useDocumentsHubFilterStore()}
          activeCount={() => useActiveDocumentsHubFilterCount()}
          options={[
            HubFilterOption.VISIBILITY,
            HubFilterOption.PROJECT,
            HubFilterOption.PARTNER,
            HubFilterOption.ROLE,
            HubFilterOption.UPLOADED_BY,
            HubFilterOption.FILTER_RANGE,
          ].map((option) => ({
            option: option,
          }))}
        />,
      ]}
    >
      {isLoading ? (
        <LoadingIndicator />
      ) : !documents || isError ? (
        <span>{t('Errors.fetch')}</span>
      ) : (
        <>
          <DocumentsTable
            documents={paginatedData}
            onAdd={() => {
              setDialogOpen(true);
            }}
            onEdit={(document) => {
              setCurrentDocument(document);
              setDialogOpen(true);
            }}
          />
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
      <DocumentUploadDialog
        isOpen={dialogOpen}
        document={
          currentDocument
            ? {
                id: currentDocument?.id,
                project_id: currentDocument?.project_id,
                org_id: currentDocument?.org_id,
                creator_id: currentDocument?.creator.id,
                title: currentDocument?.title,
                type: currentDocument?.type,
                association: currentDocument?.association,
                created_at: currentDocument?.created_at,
                file_path: currentDocument?.file_path,
                visibility: currentDocument?.visibility,
              }
            : undefined
        }
        editMode={editMode}
        onClose={() => {
          setDialogOpen(false);
          setCurrentDocument(undefined);
        }}
        onSubmitSuccess={(data) => {
          queryClient.invalidateQueries({
            queryKey: orgaDocumentsQueryKey(data.org_id),
          });
        }}
      />
    </DefaultLayout>
  );
};

export default DocumentsHubPage;
